import { useState, useEffect } from 'react';
import AdminSidebar from '../../components/feature/AdminSidebar';
import { supabase } from '../../lib/supabase';

interface Stats {
  totalCreators: number;
  totalFans: number;
  totalRevenue: number;
  monthlyTransactions: number;
  onlineUsers: number;
  pendingTransactions: number;
}

interface RecentCreator {
  id: string;
  name: string;
  avatar: string;
  subscribers: number;
  revenue: number;
  status: string;
  created_at: string;
}

interface RecentTransaction {
  id: string;
  type: string;
  from_name: string;
  to_name: string;
  amount: number;
  created_at: string;
  status: string;
}

export default function AdminDashboard() {
  const [timeRange, setTimeRange] = useState('week');
  const [stats, setStats] = useState<Stats>({
    totalCreators: 0,
    totalFans: 0,
    totalRevenue: 0,
    monthlyTransactions: 0,
    onlineUsers: 0,
    pendingTransactions: 0,
  });
  const [recentCreators, setRecentCreators] = useState<RecentCreator[]>([]);
  const [recentTransactions, setRecentTransactions] = useState<RecentTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, [timeRange]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // 통계 데이터 로드
      const [creatorsRes, fansRes, transactionsRes] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact' }).eq('user_type', 'creator'),
        supabase.from('profiles').select('*', { count: 'exact' }).eq('user_type', 'fan'),
        supabase.from('transactions').select('*'),
      ]);

      const totalRevenue = transactionsRes.data?.reduce((sum, t) => sum + (t.amount || 0), 0) || 0;
      const monthlyTransactions = transactionsRes.data?.filter(t => {
        const date = new Date(t.created_at);
        const now = new Date();
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      }).length || 0;

      const pendingCount = transactionsRes.data?.filter(t => t.status === 'pending').length || 0;

      setStats({
        totalCreators: creatorsRes.count || 0,
        totalFans: fansRes.count || 0,
        totalRevenue,
        monthlyTransactions,
        onlineUsers: Math.floor(Math.random() * 300) + 100, // 임시 데이터
        pendingTransactions: pendingCount,
      });

      // 최근 가입 크리에이터
      const { data: creators } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_type', 'creator')
        .order('created_at', { ascending: false })
        .limit(3);

      if (creators) {
        const creatorsWithStats = await Promise.all(
          creators.map(async (creator) => {
            const { count: subscriberCount } = await supabase
              .from('subscriptions')
              .select('*', { count: 'exact' })
              .eq('creator_id', creator.id)
              .eq('status', 'active');

            const { data: transactions } = await supabase
              .from('transactions')
              .select('amount')
              .eq('to_user_id', creator.id);

            const revenue = transactions?.reduce((sum, t) => sum + (t.amount || 0), 0) || 0;

            return {
              id: creator.id,
              name: creator.full_name || creator.username || '익명',
              avatar: creator.avatar_url || `https://public.readdy.ai/ai/img_res/fbaad4b71e76753bd38c13a8c2875878.jpg${creator.id}&orientation=squarish`,
              subscribers: subscriberCount || 0,
              revenue,
              status: creator.status || 'active',
              created_at: creator.created_at,
            };
          })
        );
        setRecentCreators(creatorsWithStats);
      }

      // 최근 거래 내역
      const { data: transactions } = await supabase
        .from('transactions')
        .select(`
          *,
          from_profile:profiles!transactions_from_user_id_fkey(full_name, username),
          to_profile:profiles!transactions_to_user_id_fkey(full_name, username)
        `)
        .order('created_at', { ascending: false })
        .limit(4);

      if (transactions) {
        const formattedTransactions = transactions.map((t: any) => ({
          id: t.id,
          type: t.type,
          from_name: t.from_profile?.full_name || t.from_profile?.username || '시스템',
          to_name: t.to_profile?.full_name || t.to_profile?.username || '시스템',
          amount: t.amount,
          created_at: new Date(t.created_at).toLocaleString('ko-KR'),
          status: t.status,
        }));
        setRecentTransactions(formattedTransactions);
      }
    } catch (error) {
      console.error('대시보드 데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'subscription': return 'ri-vip-crown-line';
      case 'tip': return 'ri-gift-line';
      case 'charge': return 'ri-wallet-line';
      case 'withdrawal': return 'ri-bank-card-line';
      default: return 'ri-exchange-line';
    }
  };

  const getTransactionLabel = (type: string) => {
    switch (type) {
      case 'subscription': return '구독';
      case 'tip': return '팁';
      case 'charge': return '충전';
      case 'withdrawal': return '출금';
      default: return '거래';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-teal-100 text-teal-700';
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'failed': return 'bg-red-100 text-red-700';
      case 'active': return 'bg-teal-100 text-teal-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed': return '완료';
      case 'pending': return '대기';
      case 'failed': return '실패';
      case 'active': return '활성';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-blue-50">
      <AdminSidebar />
      
      <div className="lg:ml-64 p-4 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* 헤더 */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">어드민 대시보드</h1>
            <p className="text-gray-600">플랫폼 전체 현황을 관리하세요</p>
          </div>

          {/* 기간 선택 */}
          <div className="flex gap-2 mb-6">
            {['day', 'week', 'month', 'year'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  timeRange === range
                    ? 'bg-gradient-to-r from-teal-500 to-blue-500 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {range === 'day' && '오늘'}
                {range === 'week' && '이번 주'}
                {range === 'month' && '이번 달'}
                {range === 'year' && '올해'}
              </button>
            ))}
          </div>

          {/* 통계 카드 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-user-star-line text-white text-2xl"></i>
              </div>
              <p className="text-sm text-gray-600 mb-1">총 크리에이터</p>
              <div className="flex items-end justify-between">
                <p className="text-2xl font-bold text-gray-800">{stats.totalCreators.toLocaleString()}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-user-heart-line text-white text-2xl"></i>
              </div>
              <p className="text-sm text-gray-600 mb-1">총 팬</p>
              <div className="flex items-end justify-between">
                <p className="text-2xl font-bold text-gray-800">{stats.totalFans.toLocaleString()}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-teal-600 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-white text-2xl"></i>
              </div>
              <p className="text-sm text-gray-600 mb-1">총 수익</p>
              <div className="flex items-end justify-between">
                <p className="text-2xl font-bold text-gray-800">₩{stats.totalRevenue.toLocaleString()}</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg hover:shadow-xl transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center mb-4">
                <i className="ri-exchange-line text-white text-2xl"></i>
              </div>
              <p className="text-sm text-gray-600 mb-1">이번 달 거래</p>
              <div className="flex items-end justify-between">
                <p className="text-2xl font-bold text-gray-800">{stats.monthlyTransactions.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* 최근 가입 크리에이터 */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">최근 가입 크리에이터</h2>
                <button 
                  onClick={() => window.REACT_APP_NAVIGATE('/admin/creators')}
                  className="text-teal-600 hover:text-teal-700 font-semibold text-sm cursor-pointer whitespace-nowrap"
                >
                  전체보기 →
                </button>
              </div>
              <div className="space-y-4">
                {recentCreators.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">아직 가입한 크리에이터가 없습니다</p>
                ) : (
                  recentCreators.map((creator) => (
                    <div key={creator.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-all">
                      <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                        <img src={creator.avatar} alt={creator.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800">{creator.name}</p>
                        <p className="text-sm text-gray-600">구독자 {creator.subscribers.toLocaleString()}명</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-800">₩{creator.revenue.toLocaleString()}</p>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(creator.status)}`}>
                          {getStatusLabel(creator.status)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* 최근 거래 내역 */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">최근 거래 내역</h2>
                <button 
                  onClick={() => window.REACT_APP_NAVIGATE('/admin/transactions')}
                  className="text-teal-600 hover:text-teal-700 font-semibold text-sm cursor-pointer whitespace-nowrap"
                >
                  전체보기 →
                </button>
              </div>
              <div className="space-y-4">
                {recentTransactions.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">아직 거래 내역이 없습니다</p>
                ) : (
                  recentTransactions.map((transaction) => (
                    <div key={transaction.id} className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-all">
                      <div className="w-10 h-10 bg-gradient-to-br from-teal-100 to-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className={`${getTransactionIcon(transaction.type)} text-teal-600 text-lg`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            {getTransactionLabel(transaction.type)}
                          </span>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${getStatusColor(transaction.status)}`}>
                            {getStatusLabel(transaction.status)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">{transaction.from_name} → {transaction.to_name}</p>
                        <p className="text-xs text-gray-500">{transaction.created_at}</p>
                      </div>
                      <p className="text-sm font-bold text-gray-800 whitespace-nowrap">₩{transaction.amount.toLocaleString()}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* 실시간 활동 */}
          <div className="mt-8 bg-gradient-to-r from-teal-500 via-blue-500 to-purple-500 rounded-3xl p-8 text-white shadow-xl">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-2xl font-bold mb-2">실시간 활동 모니터링</h2>
                <p className="text-white/90">현재 플랫폼에서 진행 중인 활동을 확인하세요</p>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-3xl font-bold">{stats.onlineUsers}</p>
                  <p className="text-sm text-white/80">온라인 사용자</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold">{stats.pendingTransactions}</p>
                  <p className="text-sm text-white/80">진행 중 거래</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}